import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import { Avatar, Box, keyframes } from '@mui/material'
import React, { useEffect, useRef } from 'react'

import { ChatMessage } from '../../types/chat'
import { ChatBubble } from './ChatBubble'

const bounce = keyframes`
    0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
    30% { transform: translateY(-5px); opacity: 1; }
`

interface ChatMessageListProps {
    messages: ChatMessage[]
    isLoading: boolean
}

export const ChatMessageList = ({ messages, isLoading }: ChatMessageListProps) => {
    const bottomRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages, isLoading])

    return (
        <Box
            sx={{
                flex: 1,
                overflowY: 'auto',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                '&::-webkit-scrollbar': { width: 5 },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'divider', borderRadius: '4px' },
            }}
            data-test="chat-message-list"
        >
            {messages.map((msg) => (
                <ChatBubble key={msg.id} message={msg} />
            ))}
            {isLoading && (
                <Box sx={{ display: 'flex', gap: '8px', maxWidth: '88%', alignSelf: 'flex-start' }}>
                    <Avatar
                        sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '8px',
                            backgroundColor: 'primary.main',
                            marginTop: '2px',
                        }}
                    >
                        <SmartToyOutlinedIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                    </Avatar>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            padding: '12px 16px',
                            backgroundColor: '#f0f4f8',
                            borderRadius: '4px 14px 14px 14px',
                        }}
                    >
                        {[0, 1, 2].map((i) => (
                            <Box
                                key={i}
                                sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: '50%',
                                    backgroundColor: 'text.secondary',
                                    animation: `${bounce} 1.2s ease-in-out infinite`,
                                    animationDelay: `${i * 0.15}s`,
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}
            <div ref={bottomRef} />
        </Box>
    )
}
